import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import { Schema } from "effect";

import {
  signUpWithEmail,
  signInWithGoogle,
} from "@/auth/auth-client";
import { StructuredData } from "@/shared/components/structured-data";
import { useAppForm } from "@/shared/forms/form-context";
import { FormInput } from "@/shared/forms/form-input";
import { AuthLayout } from "@/shared/layout/auth-layout";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import {
  seo,
  createPageTitle,
  getCanonicalLinks,
} from "@/utils/seo";
import { SignUpInput } from "@vivus/common";

function SignUpPage() {
  const form = useAppForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
    },
    validators: {
      onChange: Schema.toStandardSchemaV1(SignUpInput),
      onSubmitAsync: async ({ value }) => {
        try {
          await signUpWithEmail(
            value.email,
            value.password,
            value.name,
            "/app/dashboard"
          );
          return undefined;
        } catch (error) {
          return error instanceof Error
            ? error.message
            : "An error occurred during sign up";
        }
      },
    },
  });

  return (
    <AuthLayout>
      <StructuredData
        url="https://vivus.cz/sign-up"
        description="Create your vivus account to start using AI agents platform."
        about="User Registration"
        titleParts={["Sign Up"]}
      />
      <Card>
        <CardHeader className="text-center">
          <CardTitle variant="large">
            Create an account
          </CardTitle>
          <CardDescription>
            Enter your information to create an account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <div className="grid gap-6">
              <div className="flex flex-col gap-4">
                <Button
                  variant="outline"
                  className="w-full"
                  type="button"
                  onClick={() => void signInWithGoogle()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="mr-2 h-4 w-4"
                  >
                    <path
                      d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                      fill="currentColor"
                    />
                  </svg>
                  Sign In
                </Button>
              </div>
              <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                <span className="relative z-10 bg-card px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
              <div className="grid gap-4">
                <FormInput
                  form={form}
                  name="name"
                  label="Full Name"
                  placeholder="John Doe"
                  type="text"
                  required
                />
                <FormInput
                  form={form}
                  name="email"
                  label="Email"
                  placeholder="m@example.com"
                  type="email"
                  autoComplete="email"
                  required
                />
                <FormInput
                  form={form}
                  name="password"
                  label="Password"
                  type="password"
                  autoComplete="new-password"
                  required
                />
                <form.Subscribe
                  selector={(state) => [
                    state.canSubmit,
                    state.isSubmitting,
                    state.errorMap.onSubmit,
                  ]}
                >
                  {([
                    canSubmit,
                    isSubmitting,
                    submitError,
                  ]) => (
                    <>
                      {submitError && (
                        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                          {submitError}
                        </div>
                      )}
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={!canSubmit}
                      >
                        {isSubmitting
                          ? "Creating Account..."
                          : "Create Account"}
                      </Button>
                    </>
                  )}
                </form.Subscribe>
              </div>
              <div className="mt-4 text-center text-sm">
                Already have an account?{" "}
                <Link to="/sign-in" className="underline">
                  Sign in
                </Link>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}

export const Route = createFileRoute("/sign-up")({
  component: SignUpPage,
  head: () => ({
    meta: [
      ...seo({
        title: createPageTitle("Sign Up"),
        description:
          "Create your vivus account to start using AI agents platform.",
        keywords:
          "vivus signup, create account, ai agents registration, get started",
        canonical: "https://vivus.cz/sign-up",
      }),
    ],
    links: [
      ...getCanonicalLinks("https://vivus.cz/sign-up"),
    ],
  }),
});
